import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@signalvote/api-client';
import type { CampaignConsultantDto, PlaybookDto, SavePlaybookRequest, SetConsultantRequest } from '@signalvote/shared';
import { apiJson, type ApiErrorSpec } from './apiFetch';
import { queryKeys } from '@/lib/queryKeys';

const apiError =
  (fallback: string): ApiErrorSpec =>
  (body, response) =>
    new ApiError(response.status, body.code ?? null, body.error ?? body.message ?? fallback);

export const isVersionConflict = (error: unknown) => error instanceof ApiError && error.status === 409;

export function usePlaybook() {
  return useQuery({
    queryKey: queryKeys.playbook.mine(),
    queryFn: () => apiJson<PlaybookDto>('/api/assistant/playbook', { error: apiError("Couldn't load your playbook") }),
  });
}

export function useSavePlaybook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SavePlaybookRequest) =>
      apiJson<PlaybookDto>('/api/assistant/playbook', {
        method: 'PUT',
        json: request,
        error: apiError("Couldn't save your playbook. Try again."),
      }),
    onSuccess: (saved) => queryClient.setQueryData(queryKeys.playbook.mine(), saved),
    onError: (error) => {
      if (isVersionConflict(error)) queryClient.invalidateQueries({ queryKey: queryKeys.playbook.mine() });
    },
  });
}

export function useCampaignConsultant(campaignId: string) {
  return useQuery({
    queryKey: queryKeys.tenantCampaigns.consultant(campaignId),
    queryFn: () =>
      apiJson<CampaignConsultantDto>(`/api/campaigns/${campaignId}/consultant`, {
        error: apiError("Couldn't load the consultant"),
      }),
  });
}

export function useSetCampaignConsultant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ campaignId, request }: { campaignId: string; request: SetConsultantRequest }) =>
      apiJson<CampaignConsultantDto>(`/api/campaigns/${campaignId}/consultant`, {
        method: 'PUT',
        json: request,
        error: apiError("Couldn't save the consultant. Try again."),
      }),
    onSuccess: (saved, { campaignId }) =>
      queryClient.setQueryData(queryKeys.tenantCampaigns.consultant(campaignId), saved),
    onError: (error, { campaignId }) => {
      if (isVersionConflict(error)) {
        queryClient.invalidateQueries({ queryKey: queryKeys.tenantCampaigns.consultant(campaignId) });
      }
    },
  });
}
